/**
 * Documentation regarding the data format/schema can be found at:
 * https://developers.google.com/search/docs/data-types/music
 * A validator is also available at:
 * https://search.google.com/structured-data/testing-tool
 */

import { capitalize, uniqBy } from 'lodash';
import { getUrlInLocale } from './locale-utils';
import { getStringSlug } from '../selectors/profile';
import { defineMessages } from 'react-intl';

const MAX_TRACKS = 15;
const MAX_RECORDINGS = 3;

const messages = defineMessages({
  genre: {
    id: 'schema.genre',
    defaultMessage: 'Classical Music',
  },
});

export const getTrackTotalSeconds = (tracks = []) => {
  return tracks.length > 0 ? tracks.reduce((sum, { duration }) => sum + duration, 0) : -1;
};

export function formatDuration(seconds) {
  let iso = 'PT';
  if (typeof seconds !== 'number' || seconds < 0) {
    return undefined;
  }

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs) iso += `${hrs}H`;
  if (mins) iso += `${mins}M`;
  if (secs || iso === 'PT') iso += `${secs}S`;
  return iso;
}

export function artistWithSlug(artist) {
  if (Array.isArray(artist.slugs)) {
    return {
      ...artist,
      slug: getStringSlug(artist.slugs),
    };
  }
  return artist;
}

export function artistWithFunctions(artist, type) {
  if (type) {
    return {
      ...artist,
      functions: [type],
    };
  }
  return artist;
}

export function combineArtists({ conductor, ensembles = [], soloists = [] }) {
  const artists = [
    conductor ? artistWithFunctions(conductor, 'conductor') : null,
    ...ensembles.map(ensemble => artistWithFunctions(ensemble, 'ensemble')),
    ...soloists.map(({ person }) => person).map(soloist => artistWithFunctions(soloist, 'soloist')),
  ];
  return artists.filter(Boolean);
}

const buildUrl = (path, intl) => `https://app.idagio.com${getUrlInLocale(path, intl.locale)}`;

function buildBaseSchema({ type, baseUrl, listenAction = true, genre = true, intl }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': type,
  };

  if (genre) {
    schema.genre = intl.formatMessage(messages.genre);
  }

  if (baseUrl) {
    schema['@id'] = baseUrl;
    schema.url = baseUrl;
    schema.inLanguage = intl.locale;
    schema.isAccessibleForFree = true;

    if (listenAction) {
      schema.potentialAction = {
        '@type': 'ListenAction',
        'expectsAcceptanceOf': {
          '@type': 'Offer',
          'category': 'free',
        },
        'target': [
          {
            '@type': 'EntryPoint',
            'urlTemplate': baseUrl,
            'actionPlatform': [
              'https://schema.org/DesktopWebPlatform',
              'https://schema.org/IOSPlatform',
            ],
            'inLanguage': intl.locale,
          },
        ],
      };
    }
  }

  return schema;
}

export function buildPersonSchema(person, intl) {
  if (__CAPACITOR__ || __ELECTRON__) {
    return undefined;
  }

  const { name } = person;

  return {
    ...buildBaseSchema({
      type: ['Person'],
      listenAction: false,
      genre: false,
      intl,
    }),
    name,
  };
}

export function buildProfileSchema(profile, intl, { listenAction = true, genre = true } = {}) {
  if (__CAPACITOR__ || __ELECTRON__) {
    return undefined;
  }

  const { id, slug, name, functions = [], avatarImage, pathname } = profile;
  const slugToUse = slug || artistWithSlug(profile).slug;
  const description = functions.map(capitalize).join(', ');

  return {
    ...buildBaseSchema({
      type: ['CreativeWork', 'MusicGroup'],
      baseUrl: buildUrl(
        pathname ? `/${pathname}` : `/profiles/${slugToUse || id}/recordings`,
        intl
      ),
      listenAction,
      genre,
      intl,
    }),
    name,
    description: description || undefined,
    image: avatarImage || undefined,
  };
}

function buildWorkSchema(work, intl, { listenAction = true, genre = true } = {}) {
  if (__CAPACITOR__ || __ELECTRON__) {
    return undefined;
  }

  const {
    id,
    title,
    description,
    authors = [],
    composer,
    tonality,
    compositionYear,
    recordings = [],
  } = work;
  const lyricist = authors
    .filter(author => author.key === 'text' || author.key === 'libretto')
    .flatMap(author => author.persons);
  return {
    ...buildBaseSchema({
      type: ['CreativeWork', 'MusicComposition'],
      baseUrl: buildUrl(`/works/${id}`, intl),
      listenAction,
      genre,
      intl,
    }),
    name: `${title} | ${composer.name}`,
    description: description || undefined,
    composer: buildProfileSchema(composer, intl, { listenAction: false, genre: false }),
    lyricist: lyricist.length > 0 ? lyricist.map(l => buildPersonSchema(l, intl)) : undefined,
    musicalKey: tonality ? tonality.title : undefined,
    datePublished: compositionYear || undefined,
    includedComposition:
      recordings.length > 0
        ? recordings[0].tracks.map(({ piece }) => ({
            '@type': ['CreativeWork', 'MusicComposition'],
            'name': piece.title,
          }))
        : undefined,
  };
}

export function buildRecordingSchema(recording, intl, { listenAction = true, genre = true } = {}) {
  if (__CAPACITOR__ || __ELECTRON__) {
    return undefined;
  }

  const { id, work, summary, recordingDate, description, conductor, ensembles, soloists, tracks } =
    recording;
  const artists = combineArtists({ conductor, ensembles, soloists });

  return {
    ...buildBaseSchema({
      type: ['CreativeWork', 'MusicRecording'],
      baseUrl: buildUrl(`/recordings/${id}`, intl),
      listenAction,
      genre,
      intl,
    }),
    name: `${work.title} | ${work.composer.name} | ${summary} (${recordingDate.from})`,
    description: description || undefined,
    datePublished: recordingDate.from || undefined,
    byArtist:
      artists.length > 0
        ? artists.map(artist =>
            buildProfileSchema(artist, intl, { listenAction: false, genre: false })
          )
        : undefined,
    duration: formatDuration(getTrackTotalSeconds(tracks)),
    recordingOf: buildWorkSchema(work, intl),
  };
}

export function buildWorkSchemaWithRecordings(
  work,
  intl,
  { listenAction = true, genre = true } = {}
) {
  if (__CAPACITOR__ || __ELECTRON__) {
    return undefined;
  }

  const { recordings } = work;
  return {
    ...buildWorkSchema(work, intl, { listenAction, genre }),
    recordedAs:
      recordings.length > 0
        ? recordings
            .slice(0, MAX_RECORDINGS)
            .map(recording => buildRecordingSchema(recording, intl, { listenAction, genre }))
        : undefined,
  };
}

export function buildAlbumSchema(album, intl) {
  if (__CAPACITOR__ || __ELECTRON__) {
    return undefined;
  }

  const {
    slug,
    title,
    description,
    longDescription,
    imageUrl,
    publishDate,
    trackIds = [],
    tracks = [],
  } = album;
  const artists = tracks.flatMap(track => combineArtists(track.recording)); // we temporarily get the artists from the tracks because participants don't populate slugs info
  const uniqueArtists = uniqBy(artists, 'id');
  return {
    ...buildBaseSchema({
      type: ['CreativeWork', 'MusicAlbum'],
      baseUrl: buildUrl(`/albums/${slug}`, intl),
      intl,
    }),
    name: title,
    description: description || longDescription || undefined,
    image: imageUrl || undefined,
    datePublished: publishDate || undefined,
    numTracks: trackIds.length,
    byArtist:
      uniqueArtists.length > 0
        ? uniqueArtists.map(artist =>
            buildProfileSchema(artistWithFunctions(artist, artist.type), intl, {
              listenAction: false,
              genre: false,
            })
          )
        : undefined,
    track:
      tracks.length > 0
        ? tracks
            .slice(0, MAX_TRACKS)
            .map(({ recording, duration }) =>
              buildRecordingSchema({ ...recording, tracks: [{ duration }] }, intl)
            )
        : undefined,
  };
}

export function buildPlaylistSchema(playlist, intl) {
  if (__CAPACITOR__ || __ELECTRON__) {
    return undefined;
  }

  const {
    slug,
    title,
    description,
    longDescription,
    imageUrl,
    publishDate,
    trackIds = [],
    tracks = [],
    curator,
  } = playlist;
  return {
    ...buildBaseSchema({
      type: ['CreativeWork', 'MusicPlaylist'],
      baseUrl: buildUrl(`/playlists/${slug}`, intl),
      intl,
    }),
    name: title,
    description: description || longDescription || undefined,
    image: imageUrl || undefined,
    datePublished: publishDate || undefined,
    numTracks: trackIds.length,
    author: curator ? buildPersonSchema(curator, intl) : undefined,
    track:
      tracks.length > 0
        ? tracks
            .slice(0, MAX_TRACKS)
            .map(({ recording, duration }) =>
              buildRecordingSchema({ ...recording, tracks: [{ duration }] }, intl)
            )
        : undefined,
  };
}

export function buildCategorySchema(category, intl) {
  if (__CAPACITOR__ || __ELECTRON__) {
    return undefined;
  }

  const { pathname, title, description, recordings = [] } = category;
  return {
    ...buildBaseSchema({
      type: ['CreativeWork', 'MusicPlaylist'],
      baseUrl: buildUrl(pathname, intl),
      intl,
    }),
    name: title,
    description: description || undefined,
    track:
      recordings.length > 0
        ? recordings
            .slice(0, MAX_RECORDINGS)
            .map(recording => buildRecordingSchema(recording, intl))
        : undefined,
  };
}

export function buildMoodSchema(mood, intl) {
  if (__CAPACITOR__ || __ELECTRON__) {
    return undefined;
  }

  const { moodId, title, description, imageUrl, trackIds = [], tracks = [], curator } = mood;
  return {
    ...buildBaseSchema({
      type: ['CreativeWork', 'MusicPlaylist'],
      baseUrl: buildUrl(`/moods/${moodId}`, intl),
      intl,
    }),
    name: title,
    description: description || undefined,
    image: imageUrl || undefined,
    numTracks: trackIds.length,
    track:
      tracks.length > 0
        ? tracks
            .slice(0, MAX_TRACKS)
            .map(({ recording, duration }) =>
              buildRecordingSchema({ ...recording, tracks: [{ duration }] }, intl)
            )
        : undefined,
    author: curator ? buildPersonSchema(curator, intl) : undefined,
  };
}
